import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import type { StoryTracker } from '../../util/tracking';

type Timeouts = {
    read?: number;
    readTime?: number;
};
type Timestamps = {
    start?: number;
    latest?: number;
};
type RootRef = ReturnType<typeof useInView>['ref'];

const BASE_TRACKING_TIMEOUT = 2000; //ms

const useTracking = (id: number, storyTracker: StoryTracker): RootRef => {
    const track = (event: unknown) => storyTracker(`${id}`, event);
    const [rootRef, rootInView] = useInView();
    const [impressionTracked, setImpressionTracked] = useState(false);
    const [wereTrackEventsStarted, setTrackEventsStarted] = useState(false);
    const timeouts = useRef<Timeouts>({});
    const timestamps = useRef<Timestamps>({});

    const trackReadTime = useCallback((stop = false) => {
        if (!timestamps.current.latest || !timestamps.current.start) return;
        const now = new Date().getTime();
        const readTime = now - timestamps.current.latest;
        if (now - timestamps.current.start >= BASE_TRACKING_TIMEOUT) {
            track({ type: 'story_read_time', time_increment: readTime });
        }
        if (!stop) {
            timestamps.current.latest = now;
            timeouts.current.readTime = window.setTimeout(
                trackReadTime,
                Math.min(readTime * 2, 60000),
            );
        }
    }, []);

    useEffect(() => {
        if (rootInView) {
            if (!impressionTracked) {
                track({ type: 'story_impression' });
                setImpressionTracked(true);
            }
            if (!wereTrackEventsStarted) {
                timestamps.current.start = new Date().getTime();
                timestamps.current.latest = timestamps.current.start;
                timeouts.current.read = window.setTimeout(() => {
                    track({ type: 'story_read' });
                    setTrackEventsStarted(true);
                }, BASE_TRACKING_TIMEOUT);
                timeouts.current.readTime = window.setTimeout(trackReadTime, BASE_TRACKING_TIMEOUT);
            }
        } else {
            if (wereTrackEventsStarted) {
                trackReadTime(true);
            }
            Object.values(timeouts.current).forEach(t => window.clearTimeout(t));
            delete timestamps.current.start;
            delete timestamps.current.latest;
        }
    }, [rootInView]);
    useEffect(() => () => Object.values(timeouts.current).forEach(t => window.clearTimeout(t)), []);
    return rootRef;
};

export default useTracking;
